import { useMemo } from 'react';
import type { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { useRouter } from 'next/router';
import { parseCookies } from 'nookies';
import { useQuery } from 'urql';

import config, { getFullLocale } from '@packages/config';
import { localizedPathnameCacheKey } from '@packages/config/src/default';
import { ErrorBoundary, HeadMeta } from '@packages/shared';
import type { CmsDataJsonApi } from '@packages/cms-components/interfaces/jsonapi';
import { promotionBannerDataFetcher } from '@packages/modules/src/PromotionBanner/utils/promotionBannerDataFetcher';
import { getEcState, logger, useConfig } from '@packages/utilities';
import { cmsFetcher, getMainAudience } from '@packages/cms-components';
import type { CmsData } from '@packages/cms-components/interfaces';
import { graphql } from '@packages/gql/generated/shopping';
import { createUrqlServerClient } from '@packages/gql';

import { getTopLevelCategories } from '@packages/search/src/utils/getTopLevelCategories';
import { API_URL, SEARCH_SEO_API_URL } from '@packages/search/src/api/rest/endpoints';
import type {
  SearchApiRequestOptions,
  SearchSeoApiResponse,
} from '@packages/search/src/api/rest/types';
import { searchFetcher, searchSeoFetcher } from '@packages/search/src/api/rest/fetchers';
import { useSearchResult } from '@packages/search/src/hooks/useSearchResult';
import {
  PERSONALIZATION_COOKIE_NAME,
  usePersonalizationCookie,
} from '@packages/search/src/hooks/usePersonalizationCookie';
import { useRememberedPagedata } from '@packages/shared/src/hooks/useScrollRestoration';
import { decodeSharedQueryParamsForRestApi } from '@packages/search/src/queryEncoding';
import { cleanupCategoryPath } from '@packages/search/src/utils/categoryPaths';
import { extractTrackingInformation } from '@packages/search/src/utils/extractTrackingInformation';
import { SearchApiRequestContext } from '@packages/search/src/SearchApiRequestContext';
import {
  getServerSideGqlParams,
  useClientSideGqlParams,
} from '@packages/search/src/utils/gqlParams';
import {
  CategoryPageBrandOverview,
  CategoryPageCmsContentOnly,
  CategoryPageDefaultContent,
  CategoryPageLoadingFallback,
  SearchDebugPanel,
  SearchScrollTopButton,
  Tracking,
} from '@packages/search';

import type { Device } from '@packages/themes/src/default';
import { getChannelFromDevice } from '@packages/search/src/hooks/useChannel';
import { getNitroIdFromCookies } from '@packages/search/src/utils/getNitroIdFromCookies';
import NewsletterSheet from '@packages/cms-components/src/components/Forms/NewsletterSheet/NewsletterSheet';
import { getLocalizedCategoryPath } from '../../middleware.rewrites';

const CategoryPageDataDocument = graphql(/* GraphQL */ `
  query CategoryPageData($params: SearchParametersInput!, $pathname: String!) {
    category(params: $params, pathname: $pathname) {
      id
    }
  }
`);

type CategoryPageProps = Omit<
  InferGetServerSidePropsType<typeof getServerSideProps>,
  'fallback' | 'urqlState'
>;

/**
 * Page for displaying all products in a product category
 * */
export const CategoryPage: NextPage<CategoryPageProps> = ({
  topLevelCategories,
  bannerData,
  cmsContent,
  seoResponse = {},
  alternateLinks,
}) => {
  const { response, options } = useSearchResult();

  const { asPath } = useRouter();

  const gqlParams = useClientSideGqlParams();
  const variables = {
    params: gqlParams,
    pathname: 'uri' in options ? options.uri : '/', // TODO remove this fallback and replace with different solution when deprecating REST
  };

  const [{ data }] = useQuery({
    query: CategoryPageDataDocument,
    variables,
    context: useMemo(() => ({ suspense: false }), []),
  });

  const {
    host: { domain },
  } = useConfig();

  usePersonalizationCookie();

  useRememberedPagedata();

  const requestCacheKey = response?.searchresult?.information?.requestCacheKey ?? 'missing';

  const searchApiContextValue = useMemo(() => ({ requestCacheKey }), [requestCacheKey]);

  const { title, description, keywords, robots, canonicallink } =
    (seoResponse as SearchSeoApiResponse) ?? {};

  // no canonicallink from SEO API response -> use current url without params as canonical
  const canonicalUrl =
    canonicallink ||
    `https://www.${domain}${
      asPath.split('?')[0].endsWith('/') ? asPath.split('?')[0] : asPath.split('?')[0].concat('/')
    }`.toLowerCase();

  const categoryNavigation = response?.searchresult?.result?.navigation?.categoryNavigation ?? [];

  const seoInformationExists = seoResponse && Object.keys(seoResponse).length > 0;

  const { contentInformation } = response?.searchresult?.information ?? {};
  const isSisPage = contentInformation?.contentItems?.[0].position === 'NoProducts';
  const isBrandPage = response?.searchresult?.information?.isBrandView;

  if (isSisPage && !cmsContent) {
    logger.error('No CMS content for SIS page');
  }

  return (
    <SearchApiRequestContext.Provider value={searchApiContextValue}>
      {seoInformationExists && (
        <HeadMeta
          meta={{ title, description, keywords, robots }}
          links={{ canonical: canonicalUrl, alternateLinks }}
        />
      )}

      {(response === undefined || !data) && <CategoryPageLoadingFallback />}

      {/* TODO clearly delineate API responses for SIS/brand/default pages, to simplify conditionals */}
      {response && isSisPage && cmsContent && contentInformation && (
        <CategoryPageCmsContentOnly
          cmsContent={cmsContent}
          seoResponse={seoResponse}
          contentInformation={contentInformation}
          categoryNavigationItems={categoryNavigation}
        />
      )}

      {response && data && !isSisPage && isBrandPage && (
        <CategoryPageBrandOverview
          cmsContent={cmsContent}
          seoResponse={seoResponse}
          categoryNavigation={
            response?.searchresult.information.contentInformation?.hasLeftNavigation
              ? categoryNavigation
              : undefined
          }
          brands={response?.searchresult?.result.brands ?? []}
          brandHeadlines={response?.searchresult?.result.brandHeadline ?? []}
        />
      )}

      {response && data && !isSisPage && !isBrandPage && (
        <CategoryPageDefaultContent
          topLevelCategories={topLevelCategories}
          apiResponse={response}
          seoResponse={seoResponse}
          bannerData={bannerData}
          cmsContent={cmsContent}
        />
      )}

      {response && data && (
        <Tracking
          pageType={isSisPage ? 'sis' : 'productlist'}
          {...extractTrackingInformation(response, seoResponse)}
        />
      )}

      <SearchScrollTopButton />

      <SearchDebugPanel response={response} options={options} data={data} variables={variables} />
      <ErrorBoundary fallback={<span data-testid="newslettersheet-error" />}>
        <NewsletterSheet />
      </ErrorBoundary>
    </SearchApiRequestContext.Provider>
  );
};

// eslint-disable-next-line import/no-default-export
export default CategoryPage;

export const getServerSideProps = (async (context) => {
  const { query, locale: language, resolvedUrl, req, res } = context;

  const { clientId } = config;

  const childLogger = logger.child({
    page: 'CategoryPage',
    function: 'getServerSideProps',
    resolvedUrl,
    query,
    language,
    clientId,
  });

  childLogger.trace('CategoryPage.getServerSideProps started');

  const startTime = performance.now();
  const getTraceTimings = () => ({ elapsedMilliseconds: performance.now() - startTime });

  if (!resolvedUrl) {
    throw new Error('invalid route match: required query param is missing');
  }
  const categoryUri = cleanupCategoryPath(getLocalizedCategoryPath(language, resolvedUrl));
  const isBot = req?.headers?.['x-ua-bot'] !== undefined;
  const locale = getFullLocale(language);
  const cookies = parseCookies(context);
  const detectedDevice = req?.headers['x-ua-device'] as Device;

  const options: SearchApiRequestOptions = {
    locale,
    clientId,
    ...decodeSharedQueryParamsForRestApi(query),
    uri: categoryUri,
    isBot,
    personalization: cookies[PERSONALIZATION_COOKIE_NAME],
    nitroId: getNitroIdFromCookies(cookies),
    lmPromo: typeof query.lmPromo === 'string' ? query.lmPromo : undefined,
    channel: getChannelFromDevice(detectedDevice),
  };

  const seoOptions = {
    locale,
    clientId,
    uri: categoryUri,
  };

  childLogger.trace(getTraceTimings(), 'starting fetch from Search APIs (EPS + SEO)');

  const { ssrCache, ssrClient } = createUrqlServerClient();

  const variables = {
    params: getServerSideGqlParams(query, locale, req.headers, cookies),
    pathname: categoryUri,
  };

  const searchGqlQuery = ssrClient.query(CategoryPageDataDocument, variables).toPromise();

  const [searchGqlQueryResult, searchPromiseResult, seoPromiseResult] = await Promise.allSettled([
    searchGqlQuery,
    searchFetcher([API_URL, options]),
    searchSeoFetcher([SEARCH_SEO_API_URL, seoOptions]),
  ]);

  childLogger.trace(getTraceTimings(), 'completed fetch from Search APIs (EPS + SEO)');

  if (searchPromiseResult.status === 'rejected') {
    const { statusCode = 500 } = searchPromiseResult.reason?.cause ?? {};

    if (statusCode === 404) {
      return { notFound: true };
    }

    childLogger.error(
      { requestInfo: { url: API_URL, payload: options } },
      searchPromiseResult.reason,
    );

    throw new Error('Could not fetch search API results');
  }

  // always log gql errors, even if data was returned
  if (searchGqlQueryResult.status === 'fulfilled' && searchGqlQueryResult.value.error) {
    childLogger.error({ variables }, searchGqlQueryResult.value.error.message);
  }

  if (searchGqlQueryResult.status === 'rejected' || !searchGqlQueryResult.value.data) {
    const message =
      searchGqlQueryResult.status === 'rejected'
        ? searchGqlQueryResult.reason
        : 'No data returned from GraphQL API';
    childLogger.error({ variables }, message);

    throw new Error('Could not fetch search GraphQL results');
  }

  if (!searchPromiseResult.value.searchresult) {
    childLogger.error(
      { requestInfo: { url: API_URL, payload: options }, response: searchPromiseResult.value },
      'Search API response is missing searchresult',
    );

    throw new Error('Search API response is missing searchresult');
  }

  const searchResponse = searchPromiseResult.value;

  // Response with no products can happen for some category pages
  // Similar to the search result page, we return a 404 to the bot, but a regular page to the user
  // NOTE: SEARCH-2403 conditional access is added because there seem to be circumstances where the searchresult object is missing the result property, despite being mandatory
  const hasNoProducts = searchResponse.searchresult.result?.count === 0;

  if (isBot && hasNoProducts) {
    childLogger.debug('returning 404 to bot due to empty Search API response');

    res.statusCode = 404;
    res.end();

    // strictly speaking this is not a 404, but it is the closest we can get with Next.js
    return {
      notFound: true,
    };
  }

  const { redirectUrl, redirectCode, contentInformation } = searchResponse.searchresult.information;

  if (redirectUrl && redirectCode) {
    childLogger.trace(
      { redirectUrl, redirectCode, ...getTraceTimings() },
      'executing EPS redirect',
    );

    return {
      redirect: {
        destination: redirectUrl,
        statusCode: redirectCode,
      },
    };
  }

  if (seoPromiseResult.status === 'rejected') {
    childLogger.debug(
      { requestInfo: { url: SEARCH_SEO_API_URL, payload: seoOptions } },
      seoPromiseResult.reason,
    );
  }

  // NOTE in specific cases with CMS content and no nav sidebar, `result` is missing altogether
  const categoryId = searchResponse.searchresult.result?.navigation?.current?.id;

  childLogger.trace(
    getTraceTimings(),
    'starting fetch from Inspire APIs (external content + banner + promo banner',
  );

  const [cmsContentPromise, bannerDataPromise, promotionBannerPromise] = await Promise.allSettled([
    contentInformation
      ? cmsFetcher<CmsData>({
          bucketUrl: config.staticContent.apiEndpoints.bucket,
          pathname: categoryUri,
          locale,
        })
      : null,
    cmsFetcher<CmsDataJsonApi>({
      bucketUrl: config.banner.apiEndpoints.bucket,
      locale,
      pathname: categoryId ?? '',
    }),
    promotionBannerDataFetcher({ ...query, ...{ shopId: categoryId } }, req.cookies, locale),
  ]);

  childLogger.trace(
    getTraceTimings(),
    'completed fetch from Inspire APIs (external content + banner + promo banner',
  );

  if (cmsContentPromise.status === 'rejected') {
    childLogger.debug(
      { description: 'CMS Content Fetch (Categories-Page)', categoryUri },
      'Could not fetch cms content',
      cmsContentPromise.reason,
    );
  }

  if (bannerDataPromise.status === 'rejected') {
    childLogger.debug(
      { description: 'Banner Data Fetch (Categories-Page)', categoryId },
      'Could not fetch banner data',
      bannerDataPromise.reason,
    );
  }

  if (promotionBannerPromise.status === 'rejected') {
    childLogger.debug(
      { description: 'Promotion Banner Data Fetch (Categories-Page)', categoryId },
      'Could not fetch promo banner data',
      promotionBannerPromise.reason,
    );
  }

  const searchResultCacheKey = 'search-api-result' as string;
  const audience = req.cookies ? getMainAudience(getEcState(req.cookies)) : 'anonymous';
  const promotionBannerCacheKey = `promotionBannerData-${language}-${audience}`;

  childLogger.trace(getTraceTimings(), 'retrieving L1 categories');

  const topLevelCategories = getTopLevelCategories();

  const alternateLinks = searchResponse.searchresult.result?.navigation?.alternates;

  childLogger.trace(getTraceTimings(), 'CategoryPage.getServerSideProps finished, returning props');

  return {
    props: {
      urqlState: ssrCache.extractData(),
      fallback: {
        [searchResultCacheKey]: searchResponse,
        ...(promotionBannerPromise.status === 'fulfilled' && {
          [promotionBannerCacheKey]: promotionBannerPromise.value,
        }),
        ...(alternateLinks && {
          // NOTE: currently only a single alternate URL is supported for the locale switcher,
          // so even if there are multiple alternates, we only use the first one
          [localizedPathnameCacheKey]: Object.values(alternateLinks)[0],
        }),
      } as Record<string, any>,
      ...(topLevelCategories && { topLevelCategories }),
      ...(seoPromiseResult.status === 'fulfilled' &&
        seoPromiseResult.value && {
          seoResponse: seoPromiseResult.value,
        }),
      ...(cmsContentPromise.status === 'fulfilled' &&
        cmsContentPromise.value && {
          cmsContent: cmsContentPromise.value,
        }),
      ...(bannerDataPromise.status === 'fulfilled' &&
        bannerDataPromise.value && {
          bannerData: bannerDataPromise.value,
        }),
      ...(alternateLinks && { alternateLinks }),
    },
  };
}) satisfies GetServerSideProps;
